import React from 'react'
import { produce } from 'immer'
import { Provider, useDispatch as useReduxDispatch, useSelector } from 'react-redux'
import { createStore } from 'redux'
import { getItemColFileTypePairs, getItemDirectColumnRefs, getItemMappingFileType } from './helpers'
import { INITIAL_VALIDATION_FSM, validationFsmReducer } from './validationFsm'

const EMPTY_PREVIEW_CSV = {
  previewCsvStatus: 'idle',
  previewCsvHeaders: [],
  previewCsvRows: [],
  previewCsvRowCount: 0,
  previewCsvError: null
}

const initialState = {
  pageState: 'IDLE',
  currentTab: 'mapping',
  hasUnsavedChanges: false,
  isUnsavedChangesModalOpen: false,
  urlParams: {},
  meta: null,
  mappingData: {},
  skippedFields: {},
  savedMappingState: null,
  currentCategory: null,
  // 検証タブの FSM 状態（state / dataKindStatus / isProgressFull / isDataFixed）。
  // 単一の真実源。検証実行ボタンの活性判定・リフレッシュ可否・結果取得タイミングはすべて
  // `validationFsm.state` から導出する（`validationFsmReducer` の遷移表参照）。
  validationFsm: INITIAL_VALIDATION_FSM,
  currentFilter: 'all',
  rowStateMap: {},
  validationSummary: null,
  // IF02086 の validation_gate（canValidate / reason）を保持。
  // BE 側で「検証実行可否」を判定するゲート。null は未取得（ゲート判定なし）。
  validationGate: null,
  // ベースファイル設定有無（IF02095 由来、バグ#3160）。検証実行ボタンの非活性判定に使用。
  // 既定 false（未取得時は安全側＝ベース未指定扱い）。init で IF02095 を取得して反映する。
  isBaseFileSet: false,
  dataMappingPreviewUrl: null,
  validationResultRows: [],
  ...EMPTY_PREVIEW_CSV,
  errorDetailsList: [],
  warningDetailsList: [],
  lastValidationExecutedAt: null,
  lastValidationExecutedUserName: null,
  fileTypes: null,
  genericColumns: null,
  conversionTables: null,
  distinctItems: null,
  ruleAppliedFields: {}
}

/** Payload が空でない文字列か。リデューサ各所の `typeof === 'string' && payload` を 1 箇所に集約するために置く。 */
const isNonEmptyString = (v) => typeof v === 'string' && v.length > 0
/** Payload がオブジェクト（null/プリミティブでない）か。スプレッドや `Object.entries` の前段ガードとして使う。 */
const isPlainObject = (v) => v != null && typeof v === 'object'
/**
 * `genericColumns` の各列を一意識別するためのキー文字列を生成する。 `mappingData` / `skippedFields` / `ruleAppliedFields` の Map
 * キーとして横断利用しているため、 フォーマットを集中管理する必要がある。
 */
const columnKey = (c) => `${c?.genericTaskCode ?? ''}_${c?.chrofyColumnCode ?? ''}`
/** `columnKey` 経由で列インデックスを取得。`columns` が未ロード（null）でも `-1` を返して呼び出し側を簡潔に保つ。 */
const findColumnIndex = (columns, key) => (Array.isArray(columns) ? columns.findIndex((c) => columnKey(c) === key) : -1)

/**
 * `draft.genericColumns[i].hasCustomRule` を `flag` に揃える。
 *
 * 同値の場合は再代入しない（不要な参照差し替えで下流の再レンダーを誘発しないため）。 これは旧 `MERGE_PREV_MAPPING` 内の `if (... && c.hasCustomRule)` ガードと同じ意図を、
 * `CLEAR_RULE` 経由でも一貫させるための共通化。
 */
const setColumnCustomRuleFlag = (draft, key, flag) => {
  const i = findColumnIndex(draft.genericColumns, key)
  if (i === -1) return
  const current = draft.genericColumns[i]
  if (current.hasCustomRule === flag) return
  draft.genericColumns[i] = { ...current, hasCustomRule: flag }
}

/**
 * `mappingData` をディープに近い形でクローンする。
 *
 * 「保存済みスナップショット」(`savedMappingState`) を作る時に、後続編集が スナップショット側を巻き込まないよう、`items` 配列まで複製しておく。
 */
const cloneMappingEntries = (mappingData) =>
  Object.fromEntries(
    Object.entries(mappingData ?? {}).map(([k, entry]) => [
      k,
      { ...entry, items: Array.isArray(entry?.items) ? [...entry.items] : [] }
    ])
  )

/** `genericColumns` の表示用フラグ（hasCustomRule）をスナップショット用に複製する。 */
const cloneGenericColumnsForSnapshot = (genericColumns) =>
  Array.isArray(genericColumns) ? genericColumns.map((col) => ({ ...col })) : []

/** 「Restore（破棄）」「Sync（保存後）」両方から使う、保存済みスナップショットの生成。 関数化することで「保存済み = この形」という構造を 1 箇所で定義し続けられる。 */
const createSavedMappingState = (mappingData, skippedFields, ruleAppliedFields, genericColumns) => ({
  mappingData: cloneMappingEntries(mappingData),
  skippedFields: { ...(skippedFields ?? {}) },
  ruleAppliedFields: { ...(ruleAppliedFields ?? {}) },
  genericColumns: cloneGenericColumnsForSnapshot(genericColumns)
})

/** 破棄復元時に `genericColumns[i].hasCustomRule` をスナップショットへ同期する。 */
const restoreGenericColumnsFromSnapshot = (draft, snapshotColumns) => {
  if (!Array.isArray(snapshotColumns) || !Array.isArray(draft.genericColumns)) return
  const savedRuleByKey = new Map(snapshotColumns.map((c) => [columnKey(c), c.hasCustomRule === true]))
  draft.genericColumns = draft.genericColumns.map((col) => {
    const key = columnKey(col)
    if (!savedRuleByKey.has(key)) return col
    const hasCustomRule = savedRuleByKey.get(key)
    if (col.hasCustomRule === hasCustomRule) return col
    return { ...col, hasCustomRule }
  })
}

/** 最終実行日時/最終実行者のような文字列フィールドを「空白だけなら null 扱い」に正規化する。 別データ種別の値が空白として残っている場合に、UI で「未実行」と区別したいため。 */
const normalizeExecutionValue = (v) => (v != null && String(v).trim() !== '' ? v : null)

// --- per-action handlers --------------------------------------------------
// 各ハンドラは draft（immer）を直接変更する。payload に対する型ガードはハンドラ内で行う。
// 旧来は一つの巨大な switch だったが、(1) 各ケースを単独でテスト可能にする、
// (2) 何のアクションかが関数名から自明になる、を目的にハンドラ関数群に分割している。

/** 画面マウント時の一括初期化。プレビュー CSV を必ずリセットして、データ種別切替時に前回値が残らないようにする。 */
const handleInit = (draft, payload) => {
  if (!isPlainObject(payload)) return
  if (payload.meta != null) draft.meta = payload.meta
  if (payload.mappingData != null) draft.mappingData = payload.mappingData
  if (payload.skippedFields != null) draft.skippedFields = payload.skippedFields
  if (payload.genericColumns != null) draft.genericColumns = payload.genericColumns
  if (payload.fileTypes != null) draft.fileTypes = payload.fileTypes
  if (typeof payload.isBaseFileSet === 'boolean') draft.isBaseFileSet = payload.isBaseFileSet
  Object.assign(draft, EMPTY_PREVIEW_CSV)
  draft.ruleAppliedFields = payload.ruleAppliedFields != null ? { ...payload.ruleAppliedFields } : {}
  if (payload.mappingData != null && payload.skippedFields != null) {
    draft.savedMappingState = createSavedMappingState(
      payload.mappingData,
      payload.skippedFields,
      draft.ruleAppliedFields,
      payload.genericColumns ?? draft.genericColumns
    )
  }
}

/** 未保存変更の「破棄」操作で、最後に保存したスナップショットを mapping / skip / 判定ルール状態に戻す。 */
const handleRestoreSavedMapping = (draft) => {
  if (!draft.savedMappingState) return
  const snap = draft.savedMappingState
  draft.mappingData = cloneMappingEntries(snap.mappingData)
  draft.skippedFields = { ...(snap.skippedFields ?? {}) }
  draft.ruleAppliedFields = { ...(snap.ruleAppliedFields ?? {}) }
  restoreGenericColumnsFromSnapshot(draft, snap.genericColumns)
  draft.hasUnsavedChanges = false
}

/** 保存成功後に「現在の状態 = 保存済み状態」にスナップショットを取り直す。 */
const handleSyncSavedMappingState = (draft) => {
  draft.savedMappingState = createSavedMappingState(
    draft.mappingData,
    draft.skippedFields,
    draft.ruleAppliedFields,
    draft.genericColumns
  )
  draft.hasUnsavedChanges = false
}

/**
 * 検証結果（サマリ、警告/エラー一覧、プレビュー URL）を一括反映する。
 *
 * データ種別を切り替えた直後に「前のデータ種別の実行日時/実行者」が残ったまま見えないよう、 毎回 `lastExecutionXxx` を payload から再同期する設計。
 */
const handleSetValidationResult = (draft, payload) => {
  if (payload == null) return
  const summary = payload.summary
  draft.validationSummary = summary ?? null
  // データ種別を切り替えた際に前回実行日時・検証実行者が他の種別の値のまま残らないよう、毎回ペイロードに同期する
  const execAt = summary?.lastExecutionDateTime ?? summary?.last_execution_date_time ?? null
  const execBy = summary?.lastExecutionUserName ?? summary?.last_execution_user_name ?? null
  draft.lastValidationExecutedAt = normalizeExecutionValue(execAt)
  draft.lastValidationExecutedUserName = normalizeExecutionValue(execBy)
  if (payload.dataMappingPreviewUrl !== undefined) draft.dataMappingPreviewUrl = payload.dataMappingPreviewUrl
  if (payload.rows != null) draft.validationResultRows = payload.rows
  if (payload.errorDetailsList != null) draft.errorDetailsList = payload.errorDetailsList
  if (payload.warningDetailsList != null) draft.warningDetailsList = payload.warningDetailsList
  // validationGate: payload に明示的に含まれた場合のみ更新する（undefined は前回値を保持）。
  // 「結果クリア用の空ペイロード（EMPTY_VALIDATION_RESULT）」は validationGate: null を含めるので、
  // データ種別切替時にも前のゲート情報が残らない。
  if (payload.validationGate !== undefined) draft.validationGate = payload.validationGate
}

/** プレビュー CSV のヘッダ/行/件数/エラーを部分的に更新する。`undefined` は「未指定」とみなして上書きしない。 */
const handleSetPreviewCsvData = (draft, payload) => {
  if (!isPlainObject(payload)) return
  if (payload.headers != null) draft.previewCsvHeaders = payload.headers
  if (payload.rows != null) draft.previewCsvRows = payload.rows
  if (payload.rowCount != null) draft.previewCsvRowCount = payload.rowCount
  if (payload.error !== undefined) draft.previewCsvError = payload.error
}

/** ある列に対して「判定ルール適用」モードを ON にする。既存の手動マッピング設定はクリアする（ルールに置き換わるため）。 */
const handleApplyRule = (draft, payload) => {
  if (!isNonEmptyString(payload)) return
  draft.ruleAppliedFields[payload] = true
  delete draft.mappingData[payload]
  draft.hasUnsavedChanges = true
}

/** 列のルール適用フラグを解除する。 `genericColumns[i].hasCustomRule` は IF02085 由来でレスポンスに含まれているため、 UI 上で解除しても再フェッチ無しに表示と整合させる必要がある。 */
const handleClearRule = (draft, payload) => {
  if (!isNonEmptyString(payload)) return
  delete draft.ruleAppliedFields[payload]
  setColumnCustomRuleFlag(draft, payload, false)
  draft.hasUnsavedChanges = true
}

/** マッピングのモード（priority など）切替。エントリが無いと no-op。 */
const handleUpdateMappingMode = (draft, payload) => {
  const entry = draft.mappingData[payload?.fieldKey]
  if (!entry) return
  entry.mode = payload.mode
  draft.hasUnsavedChanges = true
}

/** マッピングアイテムを優先度順で並び替える。インデックスの範囲外指定は安全に無視する。 */
const handleMoveMappingItem = (draft, payload) => {
  const items = draft.mappingData[payload?.fieldKey]?.items
  if (!items) return
  const { fromIndex, toIndex } = payload
  if (fromIndex < 0 || toIndex < 0 || toIndex >= items.length) return
  const [moved] = items.splice(fromIndex, 1)
  items.splice(toIndex, 0, moved)
  draft.hasUnsavedChanges = true
}

/** マッピングアイテムを 1 件削除する。最後の 1 件を削除した場合はエントリ自体も消す（"空の設定"が残らないように）。 */
const handleDeleteMappingItem = (draft, payload) => {
  const fieldKey = payload?.fieldKey
  const entry = draft.mappingData[fieldKey]
  if (!entry || typeof payload?.mappingIndex !== 'number') return
  entry.items.splice(payload.mappingIndex, 1)
  if (entry.items.length === 0) delete draft.mappingData[fieldKey]
  draft.hasUnsavedChanges = true
}

/** マッピングアイテムを 1 件追加する。エントリが未作成なら priority モードで作成する。 */
const handleAddMappingItem = (draft, payload) => {
  const { fieldKey, item } = payload ?? {}
  if (!isNonEmptyString(fieldKey) || !isPlainObject(item)) return
  const entry = draft.mappingData[fieldKey]
  if (entry && Array.isArray(entry.items)) {
    entry.items.push(item)
  } else {
    draft.mappingData[fieldKey] = { items: [item], mode: 'priority' }
  }
  draft.hasUnsavedChanges = true
}

/** マッピングアイテムを 1 件差し替える。インデックスの範囲外は安全に無視。 */
const handleUpdateMappingItem = (draft, payload) => {
  const { fieldKey, item, mappingIndex } = payload ?? {}
  if (!isNonEmptyString(fieldKey) || !isPlainObject(item) || typeof mappingIndex !== 'number') return
  const entry = draft.mappingData[fieldKey]
  if (!entry || !Array.isArray(entry.items)) return
  if (mappingIndex < 0 || mappingIndex >= entry.items.length) return
  entry.items[mappingIndex] = item
  draft.hasUnsavedChanges = true
}

/** 列を「スキップ」する。既存のマッピング設定は破棄する（スキップとマッピングは排他的だから）。 */
const handleSkipField = (draft, payload) => {
  if (!isNonEmptyString(payload)) return
  delete draft.mappingData[payload]
  draft.skippedFields[payload] = true
  draft.hasUnsavedChanges = true
}

/** 列のスキップを解除する。 */
const handleUnskipField = (draft, payload) => {
  if (!isNonEmptyString(payload)) return
  delete draft.skippedFields[payload]
  draft.hasUnsavedChanges = true
}

// --- MERGE_PREV_MAPPING helpers (前月マッピング統合) -----------------------
// 「前月のマッピング設定を取り込む」操作は (1) 現在カテゴリ範囲の選別、
// (2) ファイルタイプによる項目フィルタ、(3) 現状クリア、(4) 3 種類のマージ、と
// 工程が多くて 1 関数に詰めると深くネストするため、工程ごとに分解している。

/**
 * 現在カテゴリ内の列キー集合と、それに対するメンバシップ述語を作る。
 *
 * `isKnown` は「サーバー側にもある列か」、`isInCategory` は「現在表示カテゴリの列か」を 別軸で問い合わせる。マージ対象の選別ロジックが各所で要るのでまとめている。
 */
const buildCurrentCategoryKeyFilter = (draft) => {
  const allKeys = new Set((draft.genericColumns ?? []).map(columnKey))
  const category = draft.currentCategory
  const inCategory = (column) => !category || String(column.genericTaskCode) === category
  const categoryKeys = new Set((draft.genericColumns ?? []).filter(inCategory).map(columnKey))
  // 当月タスク期間で有効な判定ルールを持つ列キー集合。`hasRule` は IF02083 が
  // 当月タスクの期間で有効期間の重複判定を済ませたフラグ（IF02085 由来の `hasCustomRule`
  // は保存済みの設定状態なので、有効期間の判定には使えない）。
  const activeRuleKeys = new Set(
    (draft.genericColumns ?? []).filter((column) => column?.hasRule === true).map(columnKey)
  )
  const isKnown = (k) => allKeys.has(k)
  const isInCategory = (k) => categoryKeys.has(k)
  const hasActiveRule = (k) => activeRuleKeys.has(k)
  return { categoryKeys, isKnown, isInCategory, hasActiveRule }
}

/**
 * 現在のファイルタイプ集合に基づいて、マッピングアイテム配列をフィルタする関数を返す。
 *
 * 取り込み元（前月）には現在は存在しないファイルタイプが残っている可能性があり、 そのまま入れると整合性が壊れるので絞り込みが必要。`fileType` 空文字は 「ファイル種別を問わない設定」を意味するので素通しする。
 *
 * 条件分岐・四則演算系（条件によって値を変える / 計算して求める / 複数の条件を組み合わせる）は 直値ファイル種別を持たず、条件式・結果値の AST 内に `col(ファイル種別, 列)` として参照を埋め込む。
 * これら参照（条件式＋結果値）の (ファイル種別, 列) が現タスクに 1 つでも存在しなければスキップする （#3120: バックエンド copy_previous_mapping_bulk と同一スコープ・同一判定）。
 *
 * 直値系（そのまま取り込む / 変換表 / 値変換 / 日付形式 / 時間形式）は `MappingColumnName` で列を 直接参照するため col() AST では検出できない。
 * `getItemDirectColumnRefs` で参照列を取り出し、 当月ファイルのヘッダに存在しない場合はスキップする（【2026Q1/001】: ファイル種別は当月にも存在するが 参照列が当月 CSV
 * から削除されているケースで、一覧と編集画面の表示が食い違う不具合の修正）。
 */
const buildFileTypeFilter = (fileTypes) => {
  const list = fileTypes ?? []
  const allowed = new Set(list.map((ft) => ft.mappingFileType ?? '').filter(Boolean))
  // ファイル種別 -> 列(ヘッダ)集合。col() 参照の列存在チェック用。
  const columnsByFileType = new Map(
    list.filter((ft) => ft.mappingFileType).map((ft) => [ft.mappingFileType, new Set(ft.headers ?? [])])
  )
  // 方式 A（条件式・四則演算の col() AST 参照）と方式 B（MappingColumnName による直値参照）の
  // 双方について、参照先の (ファイル種別, 列) が当月タスクに存在することを確認する。
  const refsExist = (item) =>
    [...getItemColFileTypePairs(item), ...getItemDirectColumnRefs(item)].every(([ft, col]) => {
      const cols = columnsByFileType.get(ft)
      return cols != null && cols.has(col)
    })
  return (items) => {
    if (!Array.isArray(items)) return items
    return items.filter((item) => {
      const t = getItemMappingFileType(item)
      return (t === '' || allowed.has(t)) && refsExist(item)
    })
  }
}

/** 現在カテゴリ内のマッピング・スキップ・ルール適用状態を全て初期化する。 取り込み（マージ）前にカテゴリ範囲だけクリアすることで、他カテゴリは変えずに置換する。 */
const clearCategoryState = (draft, categoryKeys) => {
  for (const k of categoryKeys) {
    delete draft.mappingData[k]
    delete draft.skippedFields[k]
    delete draft.ruleAppliedFields[k]
    setColumnCustomRuleFlag(draft, k, false)
  }
}

/** 取り込み元の `mappingData` を現在カテゴリの範囲だけ反映する。 `items` 配列は `filterItems` で現在ファイルタイプ集合に絞る。 */
const mergeMappingData = (draft, sourceMappingData, isInCategory, filterItems) => {
  for (const [k, v] of Object.entries(sourceMappingData ?? {})) {
    if (!isInCategory(k)) continue
    if (isPlainObject(v) && Array.isArray(v.items)) {
      const items = filterItems(v.items)
      // 全アイテムが除外された場合はキー自体を作らず「未設定」のままとする。
      // `clearCategoryState` でカテゴリ内は初期化済みなので、ここで skip すれば未設定が維持される。
      if (items.length === 0) continue
      draft.mappingData[k] = { ...v, items }
    } else {
      draft.mappingData[k] = v
    }
  }
}

/** 取り込み元の `skippedFields` を、サーバー既知列かつ現在カテゴリの範囲だけ反映する。 */
const mergeSkippedFields = (draft, sourceSkippedFields, isKnown, isInCategory) => {
  for (const [k, v] of Object.entries(sourceSkippedFields ?? {})) {
    if (isKnown(k) && isInCategory(k)) draft.skippedFields[k] = v
  }
}

/**
 * 取り込み元のルール適用フラグを反映し、`genericColumns[i].hasCustomRule` も同期する。 IF02085 レスポンス由来の `hasCustomRule` を UI
 * が直に参照しているため、両方を揃えないと表示がズレる。
 *
 * 当月タスク期間で有効な判定ルールが存在しない項目はコピーしない（【2026Q1/002】: 前月で判定ルールを適用した項目が、当月では有効期間外でも 「⚡ 判定ルールで自動設定」のまま残り、検証時にルールが適用されない不具合の修正）。
 * バックエンド copy_previous_mapping_bulk の `_rule_matches` / `_overlap` と同等の判定を、 IF02083 が算出済みの `hasRule` フラグで代替している。
 */
const mergeRuleAppliedFields = (draft, sourceRuleAppliedFields, isKnown, isInCategory, hasActiveRule) => {
  for (const [k, v] of Object.entries(sourceRuleAppliedFields ?? {})) {
    if (!v) continue
    if (!isKnown(k) || !isInCategory(k)) continue
    // 当月タスク期間で有効な判定ルールが無い項目はコピー対象外（未設定のまま）とする。
    if (!hasActiveRule(k)) continue
    draft.ruleAppliedFields[k] = true
    setColumnCustomRuleFlag(draft, k, true)
  }
}

/** 前月マッピングを取り込む全工程を順序つきで適用する。各工程は単一責任の小関数に分割済み。 */
const handleMergePrevMapping = (draft, payload) => {
  const { categoryKeys, isKnown, isInCategory, hasActiveRule } = buildCurrentCategoryKeyFilter(draft)
  const filterItems = buildFileTypeFilter(draft.fileTypes)

  clearCategoryState(draft, categoryKeys)
  mergeMappingData(draft, payload?.mappingData, isInCategory, filterItems)
  mergeSkippedFields(draft, payload?.skippedFields, isKnown, isInCategory)
  mergeRuleAppliedFields(draft, payload?.ruleAppliedFields, isKnown, isInCategory, hasActiveRule)
  draft.hasUnsavedChanges = true
}

// --- dispatch table -------------------------------------------------------
// switch ではなく `action.type -> handler` の単純な lookup にすることで、
// (a) 旧来の長大な switch のネスト・break ミスを排除し、
// (b) 未定義 type は単に no-op になる（旧 default: break と同じ）。
// 単純ハンドラはここでインライン化、それ以上のロジックがあるものは別関数に出している。

const HANDLERS = {
  /** ページ全体の状態フラグ（IDLE / LOADING / ERROR 等）を切り替える。null は意図しない遷移なので無視。 */
  SET_PAGE_STATE: (d, p) => {
    if (p != null) d.pageState = p
  },
  INIT: handleInit,
  /** 表示中のタブ（mapping / validation）を切り替える。 */
  SET_CURRENT_TAB: (d, p) => {
    if (p != null) d.currentTab = p
  },
  /** 未保存変更モーダルの開閉状態。誤った値で開閉状態を壊さないよう boolean 限定。 */
  SET_IS_UNSAVED_MODAL_OPEN: (d, p) => {
    if (typeof p === 'boolean') d.isUnsavedChangesModalOpen = p
  },
  /** 「保存していない変更がある」フラグ。boolean 限定で予期せぬ truthy 値による誤検出を防ぐ。 */
  SET_HAS_UNSAVED_CHANGES: (d, p) => {
    if (typeof p === 'boolean') d.hasUnsavedChanges = p
  },
  RESTORE_SAVED_MAPPING: handleRestoreSavedMapping,
  SYNC_SAVED_MAPPING_STATE: handleSyncSavedMappingState,
  SET_VALIDATION_RESULT: handleSetValidationResult,
  /** プレビュー CSV を空表示に戻す。データ種別切替やプレビュー再取得失敗時に使う。 */
  RESET_PREVIEW_CSV: (d) => Object.assign(d, EMPTY_PREVIEW_CSV),
  /** プレビュー CSV のロード状態（idle / loading / loaded / error）を切り替える。 */
  SET_PREVIEW_CSV_STATUS: (d, p) => {
    if (p != null) d.previewCsvStatus = p
  },
  SET_PREVIEW_CSV_DATA: handleSetPreviewCsvData,
  /** チャンク取得した CSV 行を末尾に追記する。空配列は no-op。 */
  APPEND_PREVIEW_CSV_ROWS: (d, p) => {
    if (Array.isArray(p) && p.length > 0) d.previewCsvRows.push(...p)
  },
  /** プレビュー CSV のエラーメッセージを保持する。null/undefined はクリア扱い。 */
  SET_PREVIEW_CSV_ERROR: (d, p) => {
    d.previewCsvError = p ?? null
  },
  /** 検証結果一覧の絞り込み条件（all / error / warning など）を切り替える。 */
  SET_CURRENT_FILTER: (d, p) => {
    if (p != null) d.currentFilter = p
  },
  /** 表示中のデータ分類カテゴリを切り替える（null = 全カテゴリ表示）。 */
  SET_CURRENT_CATEGORY: (d, p) => {
    d.currentCategory = p
  },
  /** 検証タブ FSM の遷移を進める唯一の入口。 `validationFsmReducer` は純関数で副作用を返さない（API・プレビュー操作はコーディネータ側で実施）。 */
  VALIDATION_EVENT: (d, ev) => {
    d.validationFsm = validationFsmReducer(d.validationFsm, ev ?? { type: '' })
  },
  APPLY_RULE: handleApplyRule,
  CLEAR_RULE: handleClearRule,
  UPDATE_MAPPING_MODE: handleUpdateMappingMode,
  MOVE_MAPPING_ITEM: handleMoveMappingItem,
  DELETE_MAPPING_ITEM: handleDeleteMappingItem,
  ADD_MAPPING_ITEM: handleAddMappingItem,
  UPDATE_MAPPING_ITEM: handleUpdateMappingItem,
  SKIP_FIELD: handleSkipField,
  UNSKIP_FIELD: handleUnskipField,
  MERGE_PREV_MAPPING: handleMergePrevMapping
}

/**
 * Generic Mapping 画面のリデューサ本体。 `HANDLERS` の lookup に委譲するだけの薄いシェル。immer の `produce` で 全ハンドラを「draft
 * への直接代入」スタイルで書けるようにしている。
 */
export const genericMappingReducer = (state = initialState, action) =>
  produce(state, (draft) => {
    const handler = HANDLERS[action.type]
    if (handler) handler(draft, action.payload)
  })

/**
 * @param {string} type
 * @param {any} [payload]
 */
export const useGenericMappingDispatch = () => {
  const dispatch = useReduxDispatch()
  return (type, payload) => dispatch({ type, payload })
}

/** @type {import('react-redux').TypedUseSelectorHook<typeof initialState>} */
export const useGenericMappingSelector = useSelector

/**
 * @function StoreProvider
 * @param {React.PropsWithChildren<{}>} props
 * @returns {React.ReactElement}
 */
export const StoreProvider = (props) => {
  const [store] = React.useState(() => createStore(genericMappingReducer))
  return <Provider store={store}>{props.children}</Provider>
}
